import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InboxIcon from "@mui/icons-material/Inbox";
import PublicIcon from "@mui/icons-material/Public";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useSelector } from "react-redux";

import { selectIsAuthenticated } from "@/store/slices/authSlice";
import {
  useGetPublicMockTestsQuery,
  useGetPublicMockTestBooksQuery,
} from "@/store/api/user/publicMockTestApi";

import {
  BRAND_GRADIENT,
  BRAND_PRIMARY,
  BRAND_TINT_BG,
  BRAND_TINT_BORDER,
  UNCATEGORISED,
} from "./theme";
import {
  buildStatisticsPath,
  STATISTICS_TABS,
} from "@/pages/user/MyStatistics/constants";

/**
 * A single book card on the index page. Clicking the card navigates to the
 * book detail view (`/mock-tests/books/:bookId`), where the user picks an
 * individual test to run.
 */
const BookCard = ({ book, examCount, onOpen, isOther = false }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 3,
      overflow: "hidden",
      background: "rgba(255,255,255,0.95)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(139,92,246,0.15)",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-6px)",
        boxShadow: "0 20px 40px rgba(124,58,237,0.18)",
        borderColor: "rgba(139,92,246,0.45)",
      },
    }}
  >
    <CardActionArea
      onClick={onOpen}
      sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
    >
      <Box
        sx={{
          p: 3,
          background: isOther
            ? "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)"
            : BRAND_GRADIENT,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 96,
        }}
      >
        <Box
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.2)",
            display: "inline-flex",
          }}
        >
          {isOther ? <InboxIcon /> : <MenuBookIcon />}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, lineHeight: 1.2, color: "white" }}
            noWrap
          >
            {isOther ? "Other tests" : book.title}
          </Typography>
          {!isOther && book.year != null && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.92, fontWeight: 600 }}
            >
              {book.year}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {!isOther && book.description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {book.description}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isOther
              ? "Standalone tests not part of any collection."
              : "Browse the tests in this collection."}
          </Typography>
        )}
        <Box
          sx={{
            mt: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Chip
            size="small"
            label={`${examCount} test${examCount === 1 ? "" : "s"}`}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              color: BRAND_PRIMARY,
              bgcolor: BRAND_TINT_BG,
              border: `1px solid ${BRAND_TINT_BORDER}`,
            }}
          />
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: BRAND_PRIMARY,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            View tests
            <ArrowForwardIcon fontSize="small" />
          </Box>
        </Box>
      </Box>
    </CardActionArea>
  </Card>
);

const PublicMockTestList = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // We still pull the full exam list here — but only to know how many exams
  // each book has and whether any "Other tests" (uncategorised) exist.
  // The detail view fetches the per-book exams again with its own filter so
  // we don't ship a huge payload to clients who only want to open one book.
  const { data, isLoading, isError, error } = useGetPublicMockTestsQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const { data: books = [] } = useGetPublicMockTestBooksQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [yearFilter, setYearFilter] = useState("all"); // "all" | <year> | "none"

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const items = useMemo(() => data?.items || [], [data]);

  const years = useMemo(() => {
    const set = new Set();
    books.forEach((b) => {
      if (b.year != null && b.year !== "") set.add(b.year);
    });
    return [...set].sort((a, b) => b - a);
  }, [books]);

  // Per-book counts derived from the exam list (server-side `examCount` on
  // the book may exist, but the exam list is authoritative for what the
  // guest can actually see right now).
  const { bookCards, uncategorisedCount } = useMemo(() => {
    const byBook = new Map();
    let other = 0;
    items.forEach((exam) => {
      if (exam.book && exam.book._id) {
        byBook.set(exam.book._id, (byBook.get(exam.book._id) || 0) + 1);
      } else {
        other += 1;
      }
    });
    const cards = [];
    books.forEach((b) => {
      const count = byBook.get(b._id) || 0;
      if (count > 0) {
        cards.push({ book: b, count });
        byBook.delete(b._id);
      }
    });
    // Edge: an exam references a book that isn't in the books endpoint.
    byBook.forEach((count, id) => {
      const fallback = items.find((e) => e.book?._id === id)?.book || { _id: id };
      cards.push({ book: fallback, count });
    });
    return { bookCards: cards, uncategorisedCount: other };
  }, [items, books]);

  const visibleBookCards = useMemo(() => {
    if (yearFilter === "all" || yearFilter === "none") return bookCards;
    return bookCards.filter((c) => c.book.year === yearFilter);
  }, [bookCards, yearFilter]);

  const showOtherCard =
    uncategorisedCount > 0 &&
    (yearFilter === "all" || yearFilter === "none");

  const hasContent = visibleBookCards.length > 0 || showOtherCard;

  const goToBook = (bookId) => navigate(`/mock-tests/books/${bookId}`);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="xl">
        {/* Hero header */}
        <Box
          sx={{
            mb: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            background: "white",
            boxShadow: "0 20px 40px rgba(124,58,237,0.10)",
            border: "1px solid rgba(139,92,246,0.12)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  background: BRAND_GRADIENT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 24px rgba(124,58,237,0.32)",
                }}
              >
                <PublicIcon sx={{ color: "white", fontSize: 32 }} />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    background: BRAND_GRADIENT,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1.1,
                  }}
                >
                  Public Mock Tests
                </Typography>
                <Typography variant="body1" sx={{ color: "#475569", mt: 0.5 }}>
                  Pick a collection to browse its TOEIC tests — no login required.
                </Typography>
              </Box>
            </Box>
            {isAuthenticated && (
              <Button
                startIcon={<HistoryIcon />}
                onClick={() => navigate(buildStatisticsPath(STATISTICS_TABS.PUBLIC))}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  textTransform: "none",
                  color: BRAND_PRIMARY,
                  borderColor: BRAND_PRIMARY,
                  border: "1.5px solid",
                  "&:hover": {
                    borderColor: BRAND_PRIMARY,
                    background: BRAND_TINT_BG,
                  },
                }}
              >
                My history
              </Button>
            )}
          </Box>
        </Box>

        {/* Year filter */}
        {(years.length > 0 || uncategorisedCount > 0) && (
          <Paper
            sx={{
              p: 2,
              mb: 4,
              borderRadius: 3,
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(139,92,246,0.15)",
              boxShadow: "0 6px 18px rgba(124,58,237,0.08)",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: "#475569",
                  mr: 1,
                }}
              >
                Filter by year
              </Typography>
              {(() => {
                const yearChipSx = (active) => ({
                  borderRadius: 2,
                  fontWeight: 600,
                  ...(active
                    ? {
                        background: BRAND_GRADIENT,
                        color: "white",
                        border: "none",
                        boxShadow: "0 4px 12px rgba(124,58,237,0.28)",
                      }
                    : {
                        borderColor: "rgba(139,92,246,0.4)",
                        color: BRAND_PRIMARY,
                      }),
                });
                return (
                  <>
                    <Chip
                      label="All years"
                      variant={yearFilter === "all" ? "filled" : "outlined"}
                      onClick={() => setYearFilter("all")}
                      sx={yearChipSx(yearFilter === "all")}
                    />
                    {years.map((year) => (
                      <Chip
                        key={year}
                        label={year}
                        variant={yearFilter === year ? "filled" : "outlined"}
                        onClick={() => setYearFilter(year)}
                        sx={yearChipSx(yearFilter === year)}
                      />
                    ))}
                    {uncategorisedCount > 0 && (
                      <Chip
                        icon={<InboxIcon />}
                        label="Other"
                        variant={yearFilter === "none" ? "filled" : "outlined"}
                        onClick={() => setYearFilter("none")}
                        sx={yearChipSx(yearFilter === "none")}
                      />
                    )}
                  </>
                );
              })()}
            </Stack>
          </Paper>
        )}

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: BRAND_PRIMARY }} />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error?.data?.message || "Failed to load mock tests."}
          </Alert>
        )}

        {!isLoading && !isError && !hasContent && (
          <Alert
            severity="info"
            sx={{
              borderRadius: 2,
              bgcolor: BRAND_TINT_BG,
              border: `1px solid ${BRAND_TINT_BORDER}`,
              "& .MuiAlert-icon": { color: BRAND_PRIMARY },
            }}
          >
            No public mock tests are available yet.
          </Alert>
        )}

        {!isLoading && !isError && hasContent && (
          <Grid container spacing={3}>
            {yearFilter !== "none" &&
              visibleBookCards.map(({ book, count }) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={book._id}>
                  <BookCard
                    book={book}
                    examCount={count}
                    onOpen={() => goToBook(book._id)}
                  />
                </Grid>
              ))}
            {showOtherCard && (
              <Grid item xs={12} sm={6} md={4} lg={3} key={UNCATEGORISED}>
                <BookCard
                  book={{ _id: UNCATEGORISED }}
                  examCount={uncategorisedCount}
                  onOpen={() => goToBook(UNCATEGORISED)}
                  isOther
                />
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default PublicMockTestList;
